import { ExternalLink } from 'lucide-react';
import type { ShortlistView } from '../../lib/client.ts';
import { marketplaceShortlist } from '../../lib/marketplace.ts';
import { Button } from '../ui/button.tsx';

/**
 * "Marketplace": the shortlist on the website's marketplace, in a new tab — the page of those
 * candidates' cards a hiring manager sends a client (`lib/marketplace.ts`). Candidates with no
 * marketplace code are left out and the tooltip says how many; with none, or more than the
 * website takes, the button is disabled and says why.
 */
export function MarketplaceShortlistButton({ shortlist }: { shortlist: ShortlistView }) {
  const link = marketplaceShortlist(shortlist.candidates);
  const label = `Open ${shortlist.name} in the marketplace`;
  if (link.url === null)
    return (
      // A disabled button gets no pointer events, so the reason sits on its wrapper.
      <span title={link.reason} data-testid={`marketplace-${shortlist.name}`}>
        <Button size="sm" variant="outline" disabled aria-label={`${label}: ${link.reason}`}>
          <ExternalLink /> Marketplace
        </Button>
      </span>
    );
  const note =
    link.missing > 0
      ? ` (${link.missing} without a marketplace code ${link.missing === 1 ? 'is' : 'are'} left out)`
      : '';
  return (
    <Button size="sm" variant="outline" asChild>
      <a
        href={link.url}
        target="_blank"
        rel="noopener noreferrer"
        title={`${link.codes.length} candidate${link.codes.length === 1 ? '' : 's'} on the marketplace${note}`}
        aria-label={label}
        data-testid={`marketplace-${shortlist.name}`}
      >
        <ExternalLink /> Marketplace
      </a>
    </Button>
  );
}
